import { useWorkspaceStore } from '../stores/workspaceStore'
import { roleForWorkspace, type Role } from './permissions'

/**
 * The caller's role in the workspace on screen — the ONE hook a role gate, or a displayed role,
 * reads it from: every Authority tab (`RestrictedTab`), the mention-grant card on Limits,
 * Account (gate and profile panel), Billing, TeamView, CustomerErasure and the AppShell role
 * badge (PR #237 round 5 moved the last two direct `roleForWorkspace` callers onto it). Two readers on one
 * screen consulting two sources is how one tab called the same person an owner and a member at
 * once (MAR-296 review).
 *
 * The rule is `roleForWorkspace(userMembership, workspaceId)` and nothing else. No membership loaded, or no row for this workspace in it,
 * is `null`: no role, nothing offered.
 *
 * ⚠️ **There is no `currentUser` fallback, deliberately.** PR #237 round 3 added one for
 * `userMembership === null`; round 4 found it unsafe three ways:
 * - a stale-role 403 in that state had nothing to re-read, so the fix dropped `currentUser` and
 *   the pending created rows — locking a real owner out of every tab while `WorkspaceIndex`
 *   lagged, and on reload clearing the session to onboarding;
 * - that drop path had no "whose refusal is this" check (no membership, no `clerkUserId`), so a
 *   refusal outliving a sign-out could strip the NEXT person's `currentUser`;
 * - a persisted `currentUser {role:'owner'}` kept answering owner after a server-side demotion.
 *
 * And the state it covered does not occur in the Clerk flow: `loadWorkspaceData`'s no-accounts
 * branch sets `userMembership` to `{clerkUserId, accounts: []}` (non-null), so a first-account
 * create's row merges in through `mergeMembershipRow`; every caller of `loadWorkspaceData` (App
 * ×2, CreateAccountModal, CreateWorkspaceModal) passes a Clerk user id, so its non-Clerk branch
 * is unreachable. What is left is "the membership read failed and never loaded", where failing
 * closed — "no role", as TeamView does — is correct. The MAR-250 round-2 reasoning (in
 * `roleForWorkspace`) applies unchanged. One exception closes the hole that leaves: a create
 * made in that state SEEDS the membership from the caller's own row in the create response
 * (`mergeMembershipRow`, PR #237 round 6), so the new owner is not locked out of what she just
 * made. And a stale-role refusal deletes nothing: it only triggers the memberships re-read,
 * and that read decides (PR #237 round 7 — see `PendingMembershipRow`), so a lag-shaped
 * `no_workspace_membership` cannot reopen the round-4 lockout above.
 *
 * Kept out of `permissions.ts` on purpose: that module stays free of the store.
 */
/**
 * `scope` — pass `{ workspaceId }` when the surface is asked about a workspace by PROP rather
 * than "the one on screen" (CustomerErasure is scoped by its `workspaceId` prop, and must not
 * be silently re-pointed at `currentWorkspace` — a frame where the two differ would offer an
 * erasure on one workspace's role for another). When `scope` is given its `workspaceId` is used
 * exactly, `undefined` included (→ no role); only an absent `scope` means the current workspace.
 */
export function useWorkspaceRole(
  scope?: { workspaceId: string | undefined }
): { workspaceId: string | undefined; role: Role | null } {
  const currentWorkspaceId = useWorkspaceStore((s) => s.currentWorkspace?.id)
  const userMembership = useWorkspaceStore((s) => s.userMembership)
  const workspaceId = scope ? scope.workspaceId : currentWorkspaceId
  return { workspaceId, role: roleForWorkspace(userMembership, workspaceId) }
}
